import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Sparkles, Crown, Star, Settings } from "lucide-react";

const TIER_META = {
  base: { label: "Base", accent: "text-muted-foreground", icon: Star, rate: "8%" },
  pro: { label: "Pro", accent: "text-primary", icon: Sparkles, rate: "6%" },
  enterprise: { label: "Enterprise", accent: "text-sage", icon: Crown, rate: "5%" },
};

export default function SubscriptionCard() {
  const { lang } = useLang();
  const IT = lang === "it";
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await api.get("/subscriptions/status");
      setSub(r.data);
    } catch { setSub(null); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openPortal = async () => {
    try {
      const r = await api.post("/subscriptions/portal", { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const upgrade = async (tier) => {
    try {
      const r = await api.post("/subscriptions/checkout", { tier, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  if (loading) return null;
  const tier = sub?.tier || "base";
  const meta = TIER_META[tier] || TIER_META.base;
  const Icon = meta.icon;
  const showStatus = sub?.status && sub.status !== "active";

  return (
    <div className="border border-border rounded-lg p-5 bg-card mb-6" data-testid="subscription-card">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className={`w-11 h-11 rounded-md bg-primary/10 grid place-items-center shrink-0 ${meta.accent}`}>
            <Icon className="w-5 h-5"/>
          </div>
          <div>
            <div className="uppercase tracking-[0.2em] text-[10px] text-muted-foreground mb-1">
              {IT ? "Piano attuale" : "Current plan"}
            </div>
            <div className="font-display text-2xl flex items-center gap-2">
              {meta.label}
              {showStatus && (
                <span className="text-[10px] uppercase tracking-widest bg-muted rounded-full px-2 py-0.5">
                  {sub.status}
                </span>
              )}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {IT ? "Fee prenotazioni:" : "Booking fee:"} <span className="font-display text-primary text-sm">{meta.rate}</span>
              {sub?.trial_end && (
                <span className="ml-2">
                  · {IT ? "Trial fino al" : "Trial until"} {new Date(sub.trial_end * 1000).toLocaleDateString(IT ? "it-IT" : "en-US")}
                </span>
              )}
              {sub?.current_period_end && !sub?.trial_end && (
                <span className="ml-2">
                  · {IT ? "Rinnovo" : "Renews"} {new Date(sub.current_period_end * 1000).toLocaleDateString(IT ? "it-IT" : "en-US")}
                </span>
              )}
            </div>
            {sub?.last_invoice_at && (
              <div className="mt-2 text-[11px] text-sage flex items-center gap-1.5" data-testid="last-payment-line">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-sage"/>
                {IT ? "Ultimo pagamento OK" : "Last payment OK"}
                {sub.last_invoice_amount ? (
                  <span className="text-muted-foreground">
                    · {sub.last_invoice_currency || "EUR"} {Number(sub.last_invoice_amount).toFixed(2)}
                  </span>
                ) : null}
                {sub.next_renewal_at && (
                  <span className="text-muted-foreground">
                    · {IT ? "prossimo rinnovo il" : "next renewal on"} {new Date(sub.next_renewal_at).toLocaleDateString(IT ? "it-IT" : "en-US")}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          {tier === "base" && (
            <>
              <Button size="sm" onClick={() => upgrade("pro")}
                className="bg-primary text-primary-foreground hover:bg-primary/90 lift"
                data-testid="upgrade-pro">
                <Sparkles className="w-3.5 h-3.5 mr-1"/>{IT ? "Passa a Pro" : "Upgrade to Pro"}
              </Button>
              <Link to="/pricing" className="text-[11px] text-muted-foreground hover:text-primary text-center">
                {IT ? "Confronta i piani →" : "Compare plans →"}
              </Link>
            </>
          )}
          {tier === "pro" && (
            <>
              <Button size="sm" onClick={() => upgrade("enterprise")} variant="outline" className="lift"
                data-testid="upgrade-enterprise">
                <Crown className="w-3.5 h-3.5 mr-1"/>{IT ? "Passa a Enterprise" : "Upgrade to Enterprise"}
              </Button>
              <Button size="sm" variant="ghost" onClick={openPortal} data-testid="manage-billing">
                <Settings className="w-3.5 h-3.5 mr-1"/>{IT ? "Gestisci" : "Manage"}
              </Button>
            </>
          )}
          {tier === "enterprise" && (
            <Button size="sm" variant="outline" onClick={openPortal} className="lift" data-testid="manage-billing">
              <Settings className="w-3.5 h-3.5 mr-1"/>{IT ? "Gestisci abbonamento" : "Manage subscription"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
